import { RawEntry, OverridesMap } from '../types';

export function mergeAndDeduplicate(existing: RawEntry[], incoming: RawEntry[], replaceByDate = false): RawEntry[] {
  const map = new Map<string, RawEntry>();

  if (replaceByDate) {
    // Replace mode: skip all existing entries for dates present in incoming
    const incomingDates = new Set(incoming.map(e => e.date));
    for (const entry of existing) {
      if (incomingDates.has(entry.date)) continue;
      const roundedTime = Math.round(Number(entry.time) * 10000) / 10000;
      const key = `${entry.date}|${entry.type}|${roundedTime}`;
      map.set(key, entry);
    }
  } else {
    // Merge mode: keep all existing entries
    for (const entry of existing) {
      const roundedTime = Math.round(Number(entry.time) * 10000) / 10000;
      const key = `${entry.date}|${entry.type}|${roundedTime}`;
      map.set(key, entry);
    }
  }
  
  // Add all incoming entries (overwrites exact key matches in merge mode)
  for (const entry of incoming) {
    const roundedTime = Math.round(Number(entry.time) * 10000) / 10000;
    const key = `${entry.date}|${entry.type}|${roundedTime}`;
    map.set(key, { ...entry, time: roundedTime });
  }
  
  return Array.from(map.values());
}

export async function syncFromDb(passcode: string): Promise<any | null> {
  if (!passcode) return null;
  try {
    const res = await fetch('/api/sync', {
      headers: {
        'x-passcode': passcode
      }
    });
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    console.error('Failed to sync from db:', err);
    return null;
  }
}

export async function syncToDb(passcode: string, data: {
  entries?: RawEntry[],
  overrides?: OverridesMap,
  rateType?: 'daily' | 'hourly',
  dailyRate?: string,
  hourlyRate?: string,
  clientUpdatedAt?: string
}): Promise<{ success: boolean; updatedAt?: string } | null> {
  if (!passcode) return null;
  try {
    const res = await fetch('/api/sync', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-passcode': passcode
      },
      body: JSON.stringify(data),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    console.error('Failed to sync to db:', err);
    return null;
  }
}

export interface HrxSyncResult {
  success?: boolean;
  dryRun?: boolean;
  sessionsFound: number;
  sessionsKept: number;
  sessionsSkipped: number;
  skipped: { id: string; task: string; startsAt: string; endsAt: string }[];
  entries: RawEntry[];
  dayMap: Record<string, number>;
  totalHours: number;
  error?: string;
  detail?: string;
  /** 'body' if the request body carried an explicit token, 'cache' if it
   *  was resolved from HrxSession. UI surfaces this so the user can tell
   *  whether a sync ran off their fresh paste or the cached session. */
  authSource?: 'body' | 'cache' | null;
}

// Calls /api/sync-hrx which:
//   1. pulls DTR sessions from HRX (Cookie auth) for the given date range
//   2. filters out Break sessions (configurable)
//   3. converts HRX sessions into IN/OUT RawEntry pairs (PHT)
//   4. optionally writes them to the timelog DB (default behavior)
//
// hrxCookie is optional — if empty, the server falls back to the cached
// HrxSession for this passcode (added 2026-08-04). PersonnelId is also
// optional and similarly falls back to the cached session's value.
export async function syncFromHrx(passcode: string, body: {
  hrxCookie?: string;
  startsAt: string;     // YYYY-MM-DD
  endsAt: string;       // YYYY-MM-DD
  personnelId?: string;
  includeBreaks?: boolean;
  dryRun?: boolean;
}): Promise<HrxSyncResult | null> {
  if (!passcode) return null;
  try {
    const res = await fetch('/api/sync-hrx', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-passcode': passcode
      },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) {
      return { ...(data as Partial<HrxSyncResult>), sessionsFound: 0, sessionsKept: 0, sessionsSkipped: 0, skipped: [], entries: [], dayMap: {}, totalHours: 0 } as HrxSyncResult;
    }
    return data as HrxSyncResult;
  } catch (err) {
    console.error('Failed to sync from hrx:', err);
    return null;
  }
}
